import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import { ReactComponent as ArrowDownSelectIcon } from '../../assets/images/arrow-down-select.svg';
import { ReactComponent as ClearIcon } from '../../assets/images/clear.svg';
import { Box, Tooltip } from '@mui/material';
import { CustomCheckbox } from '../CustomCheckbox';
import { useEffect, useRef, useState } from 'react';

const Option = ({ label, isSelected, ...props }) => {
  return (
    <components.Option {...props}>
      <label style={{ fontWeight: label === 'Все' ? 500 : undefined }}>
        <CustomCheckbox checked={isSelected} />
        {label}
      </label>
    </components.Option>
  );
};

Option.propTypes = {
  label: PropTypes.string,
  isSelected: PropTypes.bool
};

const MultiValue = ({ data, children, ...props }) => data.label === 'Все' ? null : <components.MultiValue {...props}>{children}</components.MultiValue>;

MultiValue.propTypes = {
  data: PropTypes.object,
  children: PropTypes.any
};

const ClearIndicator = (props) => {
  return (
    <components.ClearIndicator {...props}>
      <ClearIcon />
    </components.ClearIndicator>
  );
};

export const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <ArrowDownSelectIcon />
    </components.DropdownIndicator>
  );
};

export const CustomMultiSelect = ({ name, value, options, setFieldValue, placeholder, disabled, onChange, tooltip, width, placeholderColor, touchedBy, showAll, ...props }) => {
  const [dropdownIndicatorState, setDropdownIndicatorState] = useState('0deg');
  const [inputHeight, setInputHeight] = useState(36);

  const selectRef = useRef();

  useEffect(() => {
    if (selectRef.current) {
      let height = window.getComputedStyle(selectRef.current.controlRef).getPropertyValue('height');
      height = Number(height.substring(0, height.length - 2));
      if (height !== inputHeight) setInputHeight(height);
    } // eslint-disable-next-line
   }, [value]);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: 12,
      minHeight: 35,
      height: showAll ? undefined : 36,
      maxWidth: width || 240,
      width: width,
      border: '1px solid #EEF0F2'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: placeholderColor || '#151C22'
    }),
    valueContainer: showAll ? undefined : (provided) => ({
      ...provided,
      paddingRight: 0,
      flexWrap: 'nowrap',
    }),
    multiValue: (provided) => ({
      ...provided,
      height: 26,
      marginTop: showAll ? 6 : -1,
      marginBottom: showAll ? 6 : undefined,
      backgroundColor: '#D0DFED',
      borderRadius: '39px',
      lineHeight: '18px',
      minWidth: showAll ? undefined : 70,
      maxWidth: showAll ? undefined : 70,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      borderRadius: 39,
      '& > svg': {
        width: 15,
        height: 15
      }
    }),
    input: (provided) => ({
      ...provided,
      maxWidth: 'calc(100% - 70px)',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      position: inputHeight > 50 ? 'relative' : undefined,
      top: inputHeight > 50 ? inputHeight * 0.48 - 16.5 : undefined,
      '& > svg': {
        width: 20,
        height: 20,
        cursor: 'pointer'
      }
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      position: inputHeight > 50 ? 'relative' : undefined,
      top: inputHeight > 50 ? inputHeight * 0.48 - 16.5 : undefined,
      transform: `rotate(${dropdownIndicatorState})`
    }),
    menu: (provided) => ({
      ...provided,
      width
    })
  };

  const handleChange = (event, res) => {
    touchedBy && touchedBy();
    let currentValue;
    let items;
    switch (res.action) {
      case 'deselect-option':
        if (res.option.value === 'all') currentValue = [];
        else {
          items = value.filter(item => item.value !== res.option.value && item.value !== 'all');
          currentValue = Array.isArray(items) ? items : items ? [items] : [];
        }
        break;
      case 'remove-value':
        items = value.filter(item => item.value !== res.removedValue.value && item.value !== 'all');
        currentValue = Array.isArray(items) ? items : items ? [items] : [];
        break;
      case 'clear':
        currentValue = [];
        break;
      case 'select-option':
        if (res.option.value === 'all') currentValue = [...options];
        else {
          items = value.filter(item => item.value !== res.option.value);
          currentValue = items.length === options.length - 2 ? [...options] : items ? [res.option, ...items] : [res.option];
        }
        break;
      default:
        currentValue = value;
        break;
    }

    if (setFieldValue) setFieldValue(res.name, currentValue);
    else onChange(currentValue);
  };

  const multiSelectToReturn =
    <Select
      name={name}
      popupIcon={<ArrowDownSelectIcon />}
      closeMenuOnSelect={false}
      styles={customStyles}
      placeholder={placeholder}
      options={options || []}
      value={value || []}
      isDisabled={disabled}
      isMulti
      noOptionsMessage={() => 'Нет опций'}
      components={{ DropdownIndicator, Option, IndicatorSeparator: null, ClearIndicator, MultiValue }}
      onChange={handleChange}
      hideSelectedOptions={false}
      ref={selectRef}
      onMenuOpen={() => setDropdownIndicatorState('180deg')}
      onMenuClose={() => setDropdownIndicatorState('0deg')}
      {...props}
    />;

  return tooltip && disabled ?
    <Tooltip title={tooltip} arrow sx={{pointerEvents: 'auto'}}>
      <Box component="span" data-testid="Wrapper">{multiSelectToReturn}</Box>
    </Tooltip> :
    multiSelectToReturn;
};

CustomMultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ]),
  placeholder: PropTypes.string,
  name: PropTypes.string.isRequired,
  setFieldValue: PropTypes.func,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  tooltip: PropTypes.string,
  width: PropTypes.number,
  placeholderColor: PropTypes.string,
  touchedBy: PropTypes.func,
  showAll: PropTypes.bool
};
